"use client"

import type React from "react"
import { createContext, useContext, useState, useEffect } from "react"
import { getCookie, setCookie } from "cookies-next"
import { dictionaries, type Locale } from "@/lib/dictionaries"
import { getPreferredLocale, setLocaleInCookie } from "@/lib/language-utils"

type LanguageContextType = {
  language: Locale
  setLanguage: (language: Locale) => void
  dictionary: any
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Locale>("en-CA")
  const [dictionary, setDictionary] = useState<any>(dictionaries["en-CA"])
  const [isLoading, setIsLoading] = useState(true)

  // Function to change the language
  const setLanguage = (newLanguage: Locale) => {
    console.log("Setting language to:", newLanguage)
    setLanguageState(newLanguage)
    setDictionary(dictionaries[newLanguage] || dictionaries["en-CA"])

    setCookie("NEXT_LOCALE", newLanguage, { maxAge: 60 * 60 * 24 * 365 }) // 1 year
    setLocaleInCookie(newLanguage)
    document.documentElement.lang = newLanguage
  }

  // Load the initial language from URL parameter or cookie on mount
  useEffect(() => {
    const getInitialLanguage = (): Locale => {
      // Check for culture parameter in URL
      if (typeof window !== "undefined") {
        const urlParams = new URLSearchParams(window.location.search)
        const cultureParam = urlParams.get("culture") as Locale | null
        console.log("Culture parameter detected:", cultureParam)

        if (cultureParam && (cultureParam === "en-CA" || cultureParam === "fr-CA")) {
          // If culture parameter exists and is valid, use it and update the cookie
          console.log("Using culture parameter:", cultureParam)
          setCookie("NEXT_LOCALE", cultureParam, { maxAge: 60 * 60 * 24 * 365 }) // 1 year
          return cultureParam
        }
      }

      // Otherwise, check for cookie
      const storedLanguage = getCookie("NEXT_LOCALE") as Locale | undefined
      console.log("Cookie language:", storedLanguage)
      return storedLanguage || (getPreferredLocale() as Locale)
    }

    const initialLanguage = getInitialLanguage()
    console.log("Initial language set to:", initialLanguage)
    setLanguageState(initialLanguage)
    setDictionary(dictionaries[initialLanguage] || dictionaries["en-CA"])

    // Set the HTML lang attribute
    document.documentElement.lang = initialLanguage

    setIsLoading(false)
  }, [])

  // Listen for URL changes to detect culture parameter changes
  useEffect(() => {
    const handleUrlChange = () => {
      if (typeof window !== "undefined") {
        const urlParams = new URLSearchParams(window.location.search)
        const cultureParam = urlParams.get("culture") as Locale | null

        if (cultureParam && (cultureParam === "en-CA" || cultureParam === "fr-CA") && cultureParam !== language) {
          console.log("URL changed, updating language to:", cultureParam)
          setLanguage(cultureParam)
        }
      }
    }

    // Initial check
    handleUrlChange()

    // Listen for popstate events (back/forward navigation)
    window.addEventListener("popstate", handleUrlChange)

    return () => {
      window.removeEventListener("popstate", handleUrlChange)
    }
  }, [language])

  if (isLoading) {
    // You could return a loading indicator here if needed
    return null
  }

  return <LanguageContext.Provider value={{ language, setLanguage, dictionary }}>{children}</LanguageContext.Provider>
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider")
  }
  return context
}
